import type { Node, NodeEntry } from 'slate';
import { Editor, Element, Transforms } from 'slate';

import { getNumColumns } from 'bundles/cml/editor/components/elements/table/tableUtils';
import { TABLE_TYPES_TO_TOOLS } from 'bundles/cml/editor/normalize/constants';
import { BLOCK_TYPES } from 'bundles/cml/shared/constants';
import type { TableCellElement, TableElement, TableRowElement } from 'bundles/cml/shared/types/elementTypes';
import type { ToolsKeys } from 'bundles/cml/shared/utils/customTools';

const normalizeTableCells = (editor: Editor, tools: Set<ToolsKeys>, [node, path]: NodeEntry<Node>) => {
  if (!Element.isElement(node) || node.type !== BLOCK_TYPES.TABLE_CELL) {
    return false;
  }

  const cell = node as TableCellElement;
  const [tableRow, tableRowPath] = Editor.parent(editor, path) as NodeEntry<TableRowElement>;
  if (tableRow.type !== BLOCK_TYPES.TABLE_ROW) {
    Transforms.unwrapNodes(editor, { at: path, match: (el) => el === cell });
    return true;
  }

  const [table] = Editor.parent(editor, tableRowPath) as NodeEntry<TableElement>;
  const rowIndex = tableRowPath[tableRowPath.length - 1];

  if (!table.headless && rowIndex === 0 && !cell.header) {
    Transforms.setNodes(editor, { header: true }, { at: path });
    return true;
  }

  if ((table.headless || rowIndex > 0) && cell.header) {
    Transforms.unsetNodes(editor, 'header', { at: path });
    return true;
  }

  return false;
};

const normalizeTableRows = (editor: Editor, tools: Set<ToolsKeys>, [node, path]: NodeEntry<Node>) => {
  if (!Element.isElement(node) || node.type !== BLOCK_TYPES.TABLE_ROW) {
    return false;
  }

  const [table] = Editor.parent(editor, path) as NodeEntry<TableElement>;
  if (table.type !== BLOCK_TYPES.TABLE) {
    Transforms.unwrapNodes(editor, { at: path, match: (el) => el === node });
    return true;
  }

  const numColumns = getNumColumns(table);

  const row = node as TableRowElement;
  const rowIndex = path[path.length - 1];

  if (row.children.length < numColumns) {
    Transforms.insertNodes(
      editor,
      {
        type: BLOCK_TYPES.TABLE_CELL,
        header: (!table.headless && rowIndex === 0) || undefined,
        children: [{ type: BLOCK_TYPES.TEXT, children: [{ text: '' }] }],
      },
      { at: [...path, row.children.length] }
    );
    return true;
  }

  return false;
};

const normalizeTableTool = (editor: Editor, tools: Set<ToolsKeys>, [node, path]: NodeEntry<Node>) => {
  if (!Element.isElement(node)) {
    return false;
  }

  const tool = TABLE_TYPES_TO_TOOLS[node.type];
  if (!tool || tools.has(tool)) {
    return false;
  }

  Transforms.unwrapNodes(editor, { at: path, match: (el) => el === node });
  return true;
};

const NORMALIZERS = [normalizeTableTool, normalizeTableRows, normalizeTableCells];

export const normalizeTables = (editor: Editor, tools: Set<ToolsKeys>, nodeEntry: NodeEntry<Node>) => {
  return NORMALIZERS.some((normalizer) => normalizer(editor, tools, nodeEntry));
};
